import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  buildCanonicalAppUrl,
  setAppleSmartBanner,
} from "../../utils/appLinkFallback";
import { Constants } from "../../constants/Contants";
import "./GiveawayRedirect.scss";

export const APP_STORE_URL =
  "https://apps.apple.com/sg/app/teleport-now/id6756201717";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=sg.teleportnow.app";
export const ANDROID_APP_PACKAGE = "sg.teleportnow.app";
export const APP_OPEN_TIMEOUT_MS = 1800;
export const GIVEAWAY_QR_API_URL = "https://api.teleportapp.sg/giveaway";
export const GIVEAWAY_ATTRIBUTION_STORAGE_KEY = "teleport_giveaway_attribution";
export const GIVEAWAY_ATTRIBUTION_KEYS = [
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
];

export function getGiveawayAttribution(
  search = window.location.search,
  storage = window.sessionStorage,
) {
  let stored = {};

  try {
    stored = JSON.parse(storage.getItem(GIVEAWAY_ATTRIBUTION_STORAGE_KEY)) || {};
  } catch (_) {
    stored = {};
  }

  const params = new URLSearchParams(search);
  const attribution = GIVEAWAY_ATTRIBUTION_KEYS.reduce((result, key) => {
    const value = (params.get(key) || stored[key] || "").trim().slice(0, 500);
    if (value) result[key] = value;
    return result;
  }, {});

  try {
    storage.setItem(GIVEAWAY_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch (_) {
    // Storage can be unavailable in strict privacy modes; GA4 still uses the URL.
  }

  return attribution;
}

export function buildGiveawayDeepLink(slug, attribution = {}) {
  const params = new URLSearchParams(attribution);
  const query = params.toString();
  return buildCanonicalAppUrl(
    `/giveaway/${encodeURIComponent(slug)}`,
    query ? `?${query}` : "",
  );
}

export function buildGiveawayStoreUrl(platform, attribution = {}) {
  if (platform !== "android" || Object.keys(attribution).length === 0) {
    return platform === "ios" ? APP_STORE_URL : PLAY_STORE_URL;
  }

  const url = new URL(PLAY_STORE_URL);
  url.searchParams.set("referrer", new URLSearchParams(attribution).toString());
  return url.toString();
}

export function buildGiveawayAppLaunchUrl(platform, slug, attribution = {}) {
  const deepLink = buildGiveawayDeepLink(slug, attribution);

  if (platform !== "android") return deepLink;

  const url = new URL(deepLink);
  const storeUrl = buildGiveawayStoreUrl("android", attribution);

  return (
    `intent://${url.host}${url.pathname}${url.search}` +
    `#Intent;scheme=https;package=${ANDROID_APP_PACKAGE};` +
    "action=android.intent.action.VIEW;" +
    "category=android.intent.category.BROWSABLE;" +
    `S.browser_fallback_url=${encodeURIComponent(storeUrl)};end`
  );
}

export function trackGiveawayEvent(eventName, parameters = {}, attribution = {}) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", eventName, {
    ...attribution,
    ...parameters,
    transport_type: "beacon",
  });
}

export function detectPlatform(userAgent = "", platform = "", maxTouchPoints = 0) {
  if (/android/i.test(userAgent)) return "android";
  if (/iPad|iPhone|iPod/i.test(userAgent)) return "ios";
  if (platform === "MacIntel" && maxTouchPoints > 1) return "ios";
  return "desktop";
}

export function isValidGiveawaySlug(slug) {
  return (
    typeof slug === "string" &&
    slug.length > 0 &&
    slug.length <= 80 &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)
  );
}

const getCurrentPlatform = () =>
  detectPlatform(
    window.navigator.userAgent,
    window.navigator.platform,
    window.navigator.maxTouchPoints,
  );

const StoreBadge = ({ href, image, alt, onClick }) => (
  <a
    className="giveaway-store-link"
    href={href}
    rel="noopener noreferrer"
    onClick={onClick}
  >
    <img src={image} alt={alt} />
  </a>
);

const defaultNavigate = (url) => window.location.assign(url);

const GiveawayRedirect = ({ navigate = defaultNavigate, campaignLanding = false }) => {
  const { slug = "" } = useParams();
  const platform = useMemo(getCurrentPlatform, []);
  const attribution = useMemo(() => getGiveawayAttribution(), []);
  const validSlug = isValidGiveawaySlug(slug);
  const deepLink = validSlug
    ? buildGiveawayDeepLink(slug, attribution)
    : "";
  const appLaunchUrl = validSlug
    ? buildGiveawayAppLaunchUrl(platform, slug, attribution)
    : "";
  const campaignQrUrl = validSlug
    ? `${GIVEAWAY_QR_API_URL}/${encodeURIComponent(slug)}/qr?v=canonical-https-v1`
    : "";
  const fallbackTimer = useRef(null);
  const appOpened = useRef(false);
  const pageViewTracked = useRef(false);
  const [status, setStatus] = useState("download");
  const [campaign, setCampaign] = useState(null);
  const [campaignStatus, setCampaignStatus] = useState(
    campaignLanding ? "loading" : "idle",
  );

  const clearFallback = useCallback(() => {
    if (fallbackTimer.current) {
      window.clearTimeout(fallbackTimer.current);
      fallbackTimer.current = null;
    }
  }, []);

  const markAppOpened = useCallback(() => {
    appOpened.current = true;
    clearFallback();
  }, [clearFallback]);

  const storeUrl = buildGiveawayStoreUrl(platform, attribution);

  const trackStoreClick = useCallback(
    (storePlatform, linkUrl, navigationMethod) => {
      trackGiveawayEvent(
        storePlatform === "ios"
          ? "giveaway_appstore_click"
          : "giveaway_playstore_click",
        {
          campaign_slug: slug,
          platform,
          link_url: linkUrl,
          navigation_method: navigationMethod,
        },
        attribution,
      );
    },
    [attribution, platform, slug],
  );

  useEffect(() => {
    if (
      slug !== "free1gb" ||
      pageViewTracked.current ||
      typeof window.gtag !== "function"
    ) {
      return;
    }

    pageViewTracked.current = true;
    trackGiveawayEvent(
      "giveaway_page_view",
      { campaign_slug: slug, platform },
      attribution,
    );
  }, [attribution, platform, slug]);

  const openApp = () => {
    if (!deepLink || platform !== "android") return;

    appOpened.current = false;
    clearFallback();
    setStatus("opening");
    trackGiveawayEvent(
      "giveaway_open_app_click",
      { campaign_slug: slug, platform, link_url: deepLink },
      attribution,
    );

    fallbackTimer.current = window.setTimeout(() => {
      if (!appOpened.current && document.visibilityState === "visible") {
        trackStoreClick(platform, storeUrl, "app_open_fallback");
        navigate(storeUrl);
      }
    }, APP_OPEN_TIMEOUT_MS);

    navigate(appLaunchUrl);
  };

  useEffect(() => {
    document.title = "Open your Teleport Now giveaway";

    if (validSlug && deepLink) setAppleSmartBanner(deepLink);

    if (!validSlug) {
      setStatus("invalid");
    } else {
      setStatus("download");
    }
  }, [deepLink, validSlug]);

  useEffect(() => {
    if (!campaignLanding || !validSlug) return undefined;

    let active = true;
    setCampaignStatus("loading");
    fetch(`${Constants.baseURL}giveaway/${encodeURIComponent(slug)}/landing`)
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok || payload?.status === false) throw new Error("Giveaway not found");
        return payload?.data;
      })
      .then((data) => {
        if (!active) return;
        setCampaign(data);
        setCampaignStatus("ready");
        document.title = `${data?.title || "Teleport Now giveaway"} | Teleport Now`;
      })
      .catch(() => {
        if (!active) return;
        setCampaignStatus("missing");
        setStatus("invalid");
      });

    return () => { active = false; };
  }, [campaignLanding, slug, validSlug]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") markAppOpened();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearFallback();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [clearFallback, markAppOpened]);

  const isDesktop = platform === "desktop";

  return (
    <main className="giveaway-redirect-page">
      <section className="giveaway-redirect-card" aria-live="polite">
        <img
          className="giveaway-redirect-logo"
          src="/images/teleport-now-color-logo.png"
          alt="Teleport Now"
        />

        {campaignLanding && campaignStatus === "loading" ? (
          <>
            <div className="giveaway-plane" aria-hidden="true">✈</div>
            <p className="giveaway-redirect-eyebrow">Teleport Now giveaway</p>
            <h1>Loading giveaway…</h1>
            <p className="giveaway-redirect-copy">Checking the latest campaign details.</p>
          </>
        ) : status === "invalid" ? (
          <>
            <p className="giveaway-redirect-eyebrow">Giveaway link</p>
            <h1>This giveaway link is invalid.</h1>
            <p className="giveaway-redirect-copy">
              Check the campaign link and try again.
            </p>
          </>
        ) : campaignLanding ? (
          <>
            {campaign?.bannerImageUrl ? (
              <img
                className="giveaway-campaign-banner"
                src={campaign.bannerImageUrl}
                alt=""
              />
            ) : (
              <div className="giveaway-plane" aria-hidden="true">✈</div>
            )}
            <p className="giveaway-redirect-eyebrow">Teleport Now giveaway</p>
            <h1>{campaign?.title || "A giveaway is waiting for you"}</h1>
            {campaign?.description && (
              <p className="giveaway-redirect-copy">{campaign.description}</p>
            )}
            <p className="giveaway-claim-note">
              Download the Teleport Now app to claim this giveaway.
            </p>
            <img
              className="giveaway-campaign-qr"
              src={campaignQrUrl}
              alt="QR code for this Teleport Now giveaway"
            />
            {platform === "android" && (
              <button className="giveaway-open-button" type="button" onClick={openApp}>
                Open Teleport Now
              </button>
            )}
          </>
        ) : (
          <>
            <div className="giveaway-plane" aria-hidden="true">✈</div>
            <p className="giveaway-redirect-eyebrow">Teleport Now giveaway</p>
            <h1>
              {isDesktop
                ? "Take Teleport Now with you"
                : status === "opening"
                  ? "Opening Teleport Now…"
                  : "Get Teleport Now to continue"}
            </h1>
            <img
              className="giveaway-campaign-qr"
              src={campaignQrUrl}
              alt="QR code for this Teleport Now giveaway"
            />
            <p className="giveaway-redirect-copy">
              {isDesktop
                ? <>
                    Download <strong>Teleport Now</strong>, then open this campaign
                    link again on your phone to keep going.
                  </>
                : "Your reward will open securely in the Teleport Now app."}
            </p>

            {platform === "android" && (
              <button className="giveaway-open-button" type="button" onClick={openApp}>
                Open Teleport Now
              </button>
            )}
          </>
        )}

        <div className="giveaway-store-links">
          {(isDesktop || platform === "ios") && (
            <StoreBadge
              href={buildGiveawayStoreUrl("ios", attribution)}
              image="/images/icons/appstore.svg"
              alt="Download on the App Store"
              onClick={() =>
                trackStoreClick(
                  "ios",
                  buildGiveawayStoreUrl("ios", attribution),
                  "store_badge",
                )
              }
            />
          )}
          {(isDesktop || platform === "android") && (
            <StoreBadge
              href={buildGiveawayStoreUrl("android", attribution)}
              image="/images/icons/googleplay.svg"
              alt="Get it on Google Play"
              onClick={() =>
                trackStoreClick(
                  "android",
                  buildGiveawayStoreUrl("android", attribution),
                  "store_badge",
                )
              }
            />
          )}
        </div>

        {!isDesktop && validSlug && (
          <p className="giveaway-redirect-help">
            {platform === "ios"
              ? "If Teleport Now is installed, tap OPEN in the App Store banner above."
              : "If the app does not open, install it from the store and return to this link."}
          </p>
        )}
      </section>
    </main>
  );
};

export default GiveawayRedirect;
